import { toaster } from '@/components/ui/toaster'
import { TOAST_DURATIONS } from '@/utils/toastDurations'

const errorOptions = {
  type: 'error',
  meta: {
    closable: true,
  },
  duration: TOAST_DURATIONS.error,
}

/**
 * Chakra v3's toaster wraps its mutations in `flushSync` so the toast
 * pops synchronously. That throws "flushSync was called from inside a
 * lifecycle method" if we trigger it while React is still in a render
 * pass — which happens when a useQuery's error becomes truthy and the
 * `useEffect` that calls showError runs in the same commit. Defer the
 * actual toast call to a microtask so it always lands after the
 * current render has settled.
 */
export const showError = (
  title: string,
  options?: { description?: string },
): void => {
  if (toaster.isVisible(title)) return
  queueMicrotask(() => {
    toaster.error({
      id: title,
      title,
      ...errorOptions,
      ...options,
    })
  })
}

export const showWarning = (
  title: string,
  options?: { description?: string; duration?: number },
): void => {
  if (toaster.isVisible(title)) return
  queueMicrotask(() => {
    toaster.create({
      id: title,
      title,
      type: 'warning',
      duration: options?.duration ?? TOAST_DURATIONS.standard,
      meta: {
        closable: true,
      },
      ...options,
    })
  })
}

export const showInformation = (
  title: string,
  options?: { description?: string; duration?: number },
): void => {
  if (toaster.isVisible(title)) return
  queueMicrotask(() => {
    toaster.create({
      id: title,
      title,
      type: 'information',
      duration: options?.duration ?? TOAST_DURATIONS.standard,
      meta: {
        closable: true,
      },
      ...options,
    })
  })
}

export const loading = (
  title: string,
  options?: {
    description?: string
  },
) =>
  toaster.create({
    type: 'loading',
    title,
    ...options,
    meta: {
      closable: true,
    },
  })

export const closeToast = (toastId: string | undefined) => {
  toaster.dismiss(toastId)
}

export const updateToast = (
  toastId: string | undefined,
  options: { title?: string; description?: string },
) => {
  if (!toastId) {
    console.error('Cannot update a toast without an id')
    return
  }
  toaster.update(toastId, options)
}

/**
 * Errors that have already been surfaced to the user by
 * `mutationWithFeedback` / `mutationWithErrorFeedback`. The global
 * MutationCache.onError handler in `queryClient.ts` checks this set
 * and skips its generic "Action failed" toast for tagged errors —
 * otherwise every caller of these helpers gets two toasts (the
 * tailored one + the global fallback).
 *
 * WeakSet: error objects auto-evict when the promise chain that
 * holds them is GC'd, so no memory accumulation.
 */
const handledMutationErrors = new WeakSet<object>()

export const isMutationErrorHandled = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  handledMutationErrors.has(error)

export const tagMutationErrorAsHandled = (error: unknown): void => {
  if (typeof error === 'object' && error !== null) {
    handledMutationErrors.add(error)
  }
}

//TODO move somewhere else?
export const mutationWithFeedback = <T>(options: {
  mutation: Promise<T>
  successText: string
  errorText: string
  onSuccess?: (data: T) => void
  loadingText?: string
  hideLoadingToast?: boolean
  onError?: (error: unknown) => void | {
    overrideToast?: { title: string; description: string; duration?: number }
  }
}) => {
  // Attach a no-op rejection handler that tags the error before the
  // outer useMutation's onError sees it. Runs in the same microtask
  // round as the source promise's rejection, before `mutateAsync`'s
  // re-throw propagates up to MutationCache.onError.
  options.mutation.catch(tagMutationErrorAsHandled)

  if (options.hideLoadingToast) {
    options.mutation
      .then((data) => {
        if (options.onSuccess) options.onSuccess(data)
        queueMicrotask(() => {
          toaster.create({ type: 'success', title: options.successText })
        })
      })
      .catch((error: unknown) => {
        let toastOptions: {
          title: string
          description?: string
          duration?: number
        } = {
          title: options.errorText,
          description: error instanceof Error ? error.message : undefined,
        }
        if (options.onError) {
          const effect = options.onError(error)
          if (effect?.overrideToast) toastOptions = effect.overrideToast
        }
        queueMicrotask(() => {
          toaster.create({ ...errorOptions, ...toastOptions })
        })
      })
  } else {
    toaster.promise(options.mutation, {
      loading: { description: options.loadingText ?? 'Saving...' },
      success: (data) => {
        if (options.onSuccess) {
          options.onSuccess(data)
        }

        return { title: options.successText, description: '' }
      },
      error: (error) => {
        if (options.onError) {
          const effect = options.onError(error)
          if (effect?.overrideToast) {
            return effect.overrideToast
          }
        }
        return {
          title: options.errorText,
          description: error instanceof Error ? error.message : undefined,
          ...errorOptions,
        }
      },
    })
  }
  return options.mutation
}

export const mutationWithErrorFeedback = async <T>(options: {
  mutation: Promise<T>
  error: string
  onSuccess?: (data: T) => void
  onError?: (error: Error) => void
}) => {
  try {
    const data = await options.mutation
    if (options.onSuccess) {
      options.onSuccess(data)
    }
    return data
  } catch (error) {
    if (error instanceof Error) {
      if (options.onError) {
        options.onError(error)
      }
      showError(options.error)
    }
    tagMutationErrorAsHandled(error)
    throw error
  }
}
